/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "../CSS/ProfileDetailsView.css";
import ProfileExpandableSection from "./ProfileExpandableSection";
import underReviewIcon from "../../../assets/images/dashboard-under-review-icon.svg";
import infoIcon from "../../../assets/images/dashboard-info-icon.svg";
import editIcon from "../../../assets/images/profile-edit-icon.svg";
import verifiedIcon from "../../../assets/images/profile-verfified-icon.svg";
import saveIcon from "../../../assets/images/profile-save-icon.svg";
import ProfileNonExpandableSection from "./ProfileNonExpandableSection";
import { MENU_ITEM, PROFILE_STATUS } from "utils/constants";
import { WEB_LINK } from "utils/strings";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import { useTabBar } from "services/TabBarProvider";
import { useLogout } from "services/LogoutProvider";
import { useStorage } from "services/StorageProvider";
import { useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import LogoutDialog from "components/elements/LogoutDialog";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { CircularProgress } from "@mui/material";
import PhoneInput from "react-phone-input-2";

interface ProfileDetailsViewProps {
  isActiveMember: boolean;
}

const ProfileDetailsView: FC<ProfileDetailsViewProps> = ({
  isActiveMember,
}) => {
  const navigate = useNavigate();
  const { setSelectedTabBar } = useTabBar();
  const { handleAppExit, handleLogout } = useLogout();
  const { userProfileData, updateUserProfileData } = useStorage();
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [linkedInEditing, setLinkedInEditing] = useState(false);
  const [tempLinkedInUrl, setTempLinkedInUrl] = useState("");
  const [showLogoutDialog, setShowLogoutDialog] = useState<boolean>(false);
  const { updateUserProfile } = useWSMApi();

  const updateLinkedInMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        linkedIn_url: tempLinkedInUrl,
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      setLinkedInEditing(false);
    },
  });

  const updatePhoneMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        mobile_number: "+" + tempPhoneNumber,
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      setPhoneEditing(false);
    },
  });

  useEffect(() => {
    if (!phoneEditing) {
      setTempPhoneNumber(userProfileData?.mobile_number || "");
    }
    if (!linkedInEditing) {
      setTempLinkedInUrl(userProfileData?.linkedIn_url || "");
    }
  }, [userProfileData?.mobile_number, userProfileData?.linkedIn_url]);

  const handleBecomeMemberClick = () => {
    if (userProfileData?.credit?.status === PROFILE_STATUS.NON_MEMBER) {
      navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
        state: {
          anyData: {
            title: "Step into Sabe",
            url: WEB_LINK.BECOME_MEMBER,
          },
        },
      });
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.UNDER_REVIEW
    ) {
      setShowBecomeMemberDialog(true);
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.PAYMENT_PENDING
    ) {
      setSelectedTabBar(MENU_ITEM.MEMBERSHIP);
    }
  };

  const handlePhoneCancel = () => {
    setPhoneEditing(false);
    setTempPhoneNumber(userProfileData?.mobile_number || "");
  };

  const handlePhoneSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    updatePhoneMutation.mutate();
  };

  const handleLinkedInCancel = () => {
    setLinkedInEditing(false);
    setTempLinkedInUrl(userProfileData?.linkedIn_url || "");
  };

  const handleLinkedInSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateLinkedInMutation.mutate();
  };

  const getFooterContent = () => {
    if (userProfileData?.credit?.status === PROFILE_STATUS.NON_MEMBER) {
      return (
        <div className="profile-screen-footer-container">
          <button
            className="non-membership-button"
            onClick={handleBecomeMemberClick}
          >
            Step into Sabe
          </button>
        </div>
      );
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.UNDER_REVIEW
    ) {
      return (
        <div className="profile-screen-footer-container">
          <button
            className="profile-screen-become-member-button"
            onClick={handleBecomeMemberClick}
          >
            Step into Sabe
            <img src={underReviewIcon} alt="under-review-icon" />
          </button>
          <div className="info-container">
            <img src={infoIcon} alt="info-icon" />
            <span>
              Membership Review is{" "}
              <span style={{ color: "#C2B58C" }}>Under Way.</span>
            </span>
          </div>
        </div>
      );
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.PAYMENT_PENDING
    ) {
      return (
        <div className="profile-screen-footer-container">
          <button
            className="profile-screen-become-member-button"
            style={{ backgroundColor: "#CDD7D6" }}
            onClick={handleBecomeMemberClick}
          >
            You’re in .. Almost
          </button>
          <div className="info-container" style={{ width: "288px" }}>
            <img src={infoIcon} alt="info-icon" />
            <span>
              Membership Approved,{" "}
              <span style={{ color: "#C2B58C" }}>Complete your payment</span>
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div style={{ marginBottom: "40px" }}>
      <ProfileExpandableSection title="Personal" subTitle="Details">
        <div className="item-container">
          <span>{userProfileData?.email}</span>
          <img src={verifiedIcon} alt="verified-icon" />
        </div>
        <div
          className="item-container"
          onClick={() => !phoneEditing && setPhoneEditing(true)}
        >
          <PhoneInput
            country={"in"}
            value={
              phoneEditing
                ? tempPhoneNumber
                : userProfileData?.mobile_number || ""
            }
            onChange={(phone) => phoneEditing && setTempPhoneNumber(phone)}
            countryCodeEditable={false}
            inputProps={{
              name: "mobile",
              required: true,
              autoFocus: phoneEditing,
              autoComplete: "off",
            }}
            containerClass="profile-screen-details-custom-phone"
            inputClass="profile-screen-details-custom-phone-input"
          />
          {phoneEditing ? (
            <>
              <div className="save-button" onClick={handlePhoneSave}>
                {updatePhoneMutation?.isPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <>
                    <img src={saveIcon} alt="save-icon" /> Save
                  </>
                )}
              </div>
              <div className="cancel-button" onClick={handlePhoneCancel}>
                ✕
              </div>
            </>
          ) : (
            <img src={editIcon} alt="url-info-icon" />
          )}
        </div>
        <div
          className="item-container"
          onClick={() => !linkedInEditing && setLinkedInEditing(true)}
        >
          <input
            placeholder="Paste Your LinkedIn URL"
            value={
              linkedInEditing
                ? tempLinkedInUrl
                : userProfileData?.linkedIn_url || ""
            }
            onChange={(e) =>
              linkedInEditing && setTempLinkedInUrl(e.target.value)
            }
            readOnly={!linkedInEditing}
            type="url"
            autoComplete="url"
          />
          {linkedInEditing ? (
            <>
              <div className="save-button" onClick={handleLinkedInSave}>
                {updateLinkedInMutation?.isPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <>
                    <img src={saveIcon} alt="save-icon" /> Save
                  </>
                )}
              </div>
              <div className="cancel-button" onClick={handleLinkedInCancel}>
                ✕
              </div>
            </>
          ) : (
            <img src={editIcon} alt="url-info-icon" />
          )}
        </div>
      </ProfileExpandableSection>
      {isActiveMember && (
        <ProfileNonExpandableSection
          title="UR"
          subTitle="Keys"
          onClick={() => navigate(ROUTE_PATHS.WSM_KEYS)}
        />
      )}
      <ProfileNonExpandableSection
        title="Sabe Chat"
        subTitle="Support"
        onClick={() => handleAppExit(WEB_LINK.WHATSAPP_CHANNEL)}
      />
      <ProfileNonExpandableSection
        title="FAQ"
        subTitle=""
        onClick={() => navigate(ROUTE_PATHS.WSM_FAQ)}
      />
      <ProfileNonExpandableSection
        title="About"
        subTitle="Us"
        onClick={() => {
          navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
            state: {
              anyData: { title: "About Us", url: WEB_LINK.ABOUT_US },
            },
          });
        }}
      />
      <ProfileNonExpandableSection
        title="Terms &"
        subTitle="Conditions"
        onClick={() => {
          navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
            state: {
              anyData: {
                title: "Terms & Conditions",
                url: WEB_LINK.TERM_CONDITION,
              },
            },
          });
        }}
      />
      <ProfileNonExpandableSection
        title="Privacy"
        subTitle="policy"
        onClick={() => {
          navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
            state: {
              anyData: {
                title: "Privacy Policy",
                url: WEB_LINK.PRIVACY_POLICY,
              },
            },
          });
        }}
      />
      {userProfileData?.credit?.status !== PROFILE_STATUS.MEMBER &&
        getFooterContent()}
      <div className="profile-screen-footer-container">
        <button onClick={() => setShowLogoutDialog(true)}>Logout</button>
      </div>
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
      <LogoutDialog
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onYes={() => handleLogout()}
      />
    </div>
  );
};

export default ProfileDetailsView;
